import React, { useState, useEffect, useCallback } from 'react';
import { additionsApi } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { Plus, Pencil, Trash2, PlusCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getWeekRange } from '../lib/utils';
import UnitSelectItems from './UnitSelectItems';

const TYPE_LABELS = {
  bonus: { label: 'Bonus', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
  refund: { label: 'Refund', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  adjustment: { label: 'Adjustment', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
  overcharge: { label: 'Overcharge Return', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300' },
  safety: { label: 'Safety', color: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300' },
  custom: { label: 'Custom', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300' },
};

/**
 * AdditionsPanel — CRUD for weekly settlement additions (bonuses, refunds, adjustments).
 *
 * Additions increase a unit's net payout on the settlement summary. They are scoped
 * to a specific unit + week (auto-derived from the addition's date).
 *
 * Props:
 *   units:         array of { id, unit_number } — for the unit dropdown
 *   selectedUnit:  'all' | unit_id — the currently filtered unit
 *   weekStart:     Monday of the currently viewed pay week (YYYY-MM-DD)
 *   canEdit:       boolean — hides add/edit/delete when false
 */
export default function AdditionsPanel({ units, selectedUnit, weekStart, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    unit_id: '',
    amount: '',
    date: '',
    addition_type: 'bonus',
    custom_type_label: '',
    notes: '',
    linked_load_reference: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { week_start: weekStart };
      if (selectedUnit && selectedUnit !== 'all') params.unit_id = selectedUnit;
      const res = await additionsApi.list(params);
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load additions');
    } finally {
      setLoading(false);
    }
  }, [selectedUnit, weekStart]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    // Default to today (clamped to the current pay week if inside it)
    const today = new Date().toISOString().split('T')[0];
    setEditing(null);
    setForm({
      unit_id: selectedUnit !== 'all' ? selectedUnit : '',
      amount: '',
      date: today >= weekStart ? today : weekStart,
      addition_type: 'bonus',
      custom_type_label: '',
      notes: '',
      linked_load_reference: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      unit_id: row.unit_id,
      amount: String(row.amount ?? ''),
      date: row.date,
      addition_type: row.addition_type || 'bonus',
      custom_type_label: row.custom_type_label || '',
      notes: row.notes || '',
      linked_load_reference: row.linked_load_reference || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.unit_id) return toast.error('Please pick a unit');
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt === 0) return toast.error('Enter a non-zero amount');
    if (!form.date) return toast.error('Please choose a date');
    if (form.addition_type === 'custom' && !form.custom_type_label.trim()) {
      return toast.error('Enter a label for the custom type');
    }

    setSaving(true);
    try {
      const payload = {
        unit_id: form.unit_id,
        amount: amt,
        date: form.date,
        addition_type: form.addition_type,
        custom_type_label: form.addition_type === 'custom' ? form.custom_type_label.trim() : null,
        notes: form.notes || null,
        linked_load_reference: form.linked_load_reference || null,
      };
      if (editing) {
        await additionsApi.update(editing.id, payload);
        toast.success('Addition updated');
      } else {
        await additionsApi.create(payload);
        toast.success('Addition added');
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete this ${row.addition_type} of ${formatCurrency(row.amount)}?`)) return;
    try {
      await additionsApi.delete(row.id);
      toast.success('Addition deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const getUnitNumber = (unitId) => units.find(u => u.id === unitId)?.unit_number || '—';

  const total = rows.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="additions-panel">
      {/* Summary + Add button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Additions for <strong>{getWeekRange(weekStart)}</strong>
            {selectedUnit !== 'all' && (
              <> · Unit <strong>{getUnitNumber(selectedUnit)}</strong></>
            )}
          </p>
          <p className="text-2xl font-bold font-mono text-emerald-600 mt-1" data-testid="additions-total">
            +{formatCurrency(total)}
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openCreate}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-addition-btn"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Addition
          </Button>
        )}
      </div>

      {/* Rows */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Linked Load</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-10 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <DollarSign className="h-8 w-8 text-muted-foreground/50" />
                        <p>No additions for this week.</p>
                        {canEdit && (
                          <p className="text-xs">
                            Use <strong>Add Addition</strong> to log a bonus, refund, or one-off adjustment.
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const t = TYPE_LABELS[row.addition_type] || TYPE_LABELS.adjustment;
                    const displayLabel = row.addition_type === 'custom' && row.custom_type_label
                      ? row.custom_type_label
                      : t.label;
                    return (
                      <TableRow key={row.id} data-testid={`addition-row-${row.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">{formatDate(row.date)}</TableCell>
                        <TableCell className="font-medium">{getUnitNumber(row.unit_id)}</TableCell>
                        <TableCell>
                          <Badge className={t.color}>{displayLabel}</Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[280px] truncate">{row.notes || '—'}</TableCell>
                        <TableCell className="text-sm font-mono text-muted-foreground">{row.linked_load_reference || '—'}</TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${row.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {row.amount >= 0 ? '+' : ''}{formatCurrency(row.amount)}
                        </TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEdit(row)}
                                data-testid={`edit-addition-${row.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(row)}
                                data-testid={`delete-addition-${row.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-600" />
              {editing ? 'Edit Addition' : 'Add Addition'}
            </DialogTitle>
            <DialogDescription>
              Adds an adjustment (bonus / refund / correction) that boosts the unit&apos;s net settlement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="addition-unit">Unit *</Label>
                <Select
                  value={form.unit_id}
                  onValueChange={(v) => setForm({ ...form, unit_id: v })}
                >
                  <SelectTrigger id="addition-unit" data-testid="addition-unit-select">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <UnitSelectItems units={units} hideInactive />
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="addition-type">Type *</Label>
                <Select
                  value={form.addition_type}
                  onValueChange={(v) => setForm({ ...form, addition_type: v })}
                >
                  <SelectTrigger id="addition-type" data-testid="addition-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonus">Bonus</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="overcharge">Overcharge Return</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="custom">Custom (enter label)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Custom type label — only when type=custom */}
            {form.addition_type === 'custom' && (
              <div className="space-y-1.5" data-testid="addition-custom-label-row">
                <Label htmlFor="addition-custom-label">Custom label *</Label>
                <Input
                  id="addition-custom-label"
                  placeholder="e.g. Detention, Layover, Referral, Toll reimbursement"
                  value={form.custom_type_label}
                  onChange={(e) => setForm({ ...form, custom_type_label: e.target.value })}
                  data-testid="addition-custom-label-input"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="addition-date">Date *</Label>
                <Input
                  id="addition-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="addition-date-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="addition-amount">Amount ($) *</Label>
                <Input
                  id="addition-amount"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 150.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="addition-amount-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="addition-linked-load">Linked Load Ref (optional)</Label>
              <Input
                id="addition-linked-load"
                placeholder="e.g. BLK-1234"
                value={form.linked_load_reference}
                onChange={(e) => setForm({ ...form, linked_load_reference: e.target.value })}
                data-testid="addition-linked-load-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="addition-notes">Notes / Description</Label>
              <Textarea
                id="addition-notes"
                rows={3}
                placeholder="e.g. On-time delivery bonus from broker XYZ"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="addition-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="addition-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="addition-save"
            >
              {saving ? 'Saving...' : (editing ? 'Save changes' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
